import * as React from "react";

import { Box, makeStyles, Theme } from "@material-ui/core";
import { useQuery } from "react-query";
import * as Redux from "react-redux";

import { Link } from "client/components";
import {
  BankLegalAgreement,
  isEligibleForPlaid,
} from "client/domain/FormBuilder/BankInformation";
import {
  ConnectorButton,
  ConnectorHeading,
  ListItem,
  ManualBankDialog,
} from "client/domain/FormBuilder/BankInformation/BankConnector";
import type { SupportedBankConnectors } from "client/domain/FormBuilder/BankInformation/BankConnector";
import {
  useMerchantModel,
  useRunOnce,
  useSaveFormState,
  useTranslation,
} from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { BankFieldSetNames } from "client/lib/apt-merchant/src/fieldsets";
import { BusinessType, CountryList } from "client/lib/apt-merchant/src/types";
import { BankService } from "client/services";
import { errorActions } from "client/store";

const useStyles = makeStyles((theme: Theme) => {
  const {
    connectorContainer,
    formFooterBorder,
  } = useNormalizedBrandPaletteValues(theme);

  return {
    skipContainer: {
      padding: theme.spacing(3),
    },
    connectorContainer: {
      border: formFooterBorder,
      backgroundColor: connectorContainer,
      borderRadius: "12px",
      padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    },
  };
});

export const determineConnectorEligibility = (
  registeredCountry: CountryList | undefined,
  businessType: BusinessType | undefined
): SupportedBankConnectors | null => {
  if (isEligibleForPlaid({ businessType, registeredCountry })) {
    return "Plaid";
  }
  return null;
};

type BankConnectorProps = {
  fallbackForm?: React.ReactNode;
  fieldSetName: BankFieldSetNames;
  updateToNextStep?: () => void | undefined;
};

export const BankConnector: React.FC<BankConnectorProps> = ({
  fallbackForm,
  fieldSetName,
  updateToNextStep,
}) => {
  const { merchant } = useMerchantModel();
  const dispatch = Redux.useDispatch();
  const classes = useStyles();
  const t = useTranslation();

  useRunOnce(() =>
    logAmplitudeEvent("REDESIGN_LINK_YOUR_BANK_ACCOUNT", "loaded_bank_form")
  );

  const businessType = merchant.companyInformation.businessType?.value;
  const registeredCountry =
    merchant.companyInformation.registeredCountry?.value;
  const storeName = merchant.storeDetails.storeName?.value;

  const plaidEnabled = () =>
    connector === "Plaid" && !!plaidResponse?.linkToken;

  const [
    connector,
    setConnector,
  ] = React.useState<SupportedBankConnectors | null>(
    determineConnectorEligibility(registeredCountry, businessType)
  );

  React.useEffect(() => {
    const connector = determineConnectorEligibility(
      registeredCountry,
      businessType
    );
    setConnector(connector);
  }, [registeredCountry, businessType]);

  // Query to grab the Plaid token if Plaid is the
  // required connector
  const { data: plaidResponse = null, isLoading: isPlaidLoading } = useQuery<
    BankService.GetPlaidTokenResponse,
    Error
  >(
    ["plaid-link-token", storeName],
    () => BankService.getPlaidLinkToken({ storeName }),
    {
      enabled: connector === "Plaid" && Boolean(storeName),
      onSuccess: ({ error }) => {
        if (error?.plaidError) {
          dispatch(errorActions.setError(error.plaidError));
        }
      },
      onError: (err) => {
        dispatch(errorActions.setError(err.message));
      },
    }
  );

  const { saveFormState } = useSaveFormState();

  const handleSkip = () => {
    // If skipping the second form, make API call to /api/update-form-completion to
    // update bankForm:true directly in order to route to customer support form
    saveFormState({ bankForm: true });
  };

  if (isPlaidLoading) {
    return null;
  }

  if (plaidEnabled()) {
    return (
      <Box>
        <Box mb={2}>
          <ConnectorHeading merchant={merchant} fieldSetName={fieldSetName} />
        </Box>
        <Box className={classes.connectorContainer}>
          <Box mb={2}>
            <ListItem>{t("uiPlaidBulletPoint1")}</ListItem>
            <ListItem>{t("uiPlaidBulletPoint2")}</ListItem>
            <ListItem>{t("uiPlaidBulletPoint3")}</ListItem>
          </Box>
          {plaidEnabled() && (
            <Box mb={2}>
              <BankLegalAgreement
                agreementType="plaid"
                country={registeredCountry}
              />
            </Box>
          )}
          <Box mb={1}>
            <ConnectorButton
              connector={connector}
              merchant={merchant}
              overrideSubmitHandler={updateToNextStep}
              plaidToken={plaidResponse?.linkToken}
              fieldSetName={fieldSetName}
            />
          </Box>
          <ManualBankDialog
            plaidEnabled={plaidEnabled()}
            fieldSetName={fieldSetName}
            updateToNextStep={updateToNextStep}
          />
        </Box>
        {fieldSetName === "SecondaryBankFieldSet" && (
          <Box
            alignContent="center"
            className={classes.skipContainer}
            display="flex"
            justifyContent="center"
          >
            <Link linkText="Skip this step" onClick={handleSkip} />
          </Box>
        )}
      </Box>
    );
  }

  return <>{fallbackForm}</>;
};
